class CollectionFilter extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.controller = new AbortController();
    this.activeFilters = new Map();
    this.currentSort = '';
    this.filterIconPlus = null;
    this.filterIconMinus = null;
    this._preventScroll = (e) => {
      if (!this.panel.contains(e.target)) e.preventDefault();
    };
  }

  connectedCallback() {
    this.panel = this.querySelector('[data-filter-panel]');
    this.overlay = this.querySelector('[data-filter-overlay]');
    this.closeButton = this.querySelector('[data-filter-close]');
    this.clearButtons = this.querySelectorAll('[data-filter-clear]');
    this.applyButtons = this.querySelectorAll('[data-filter-apply]');
    this.applyButtonTexts = this.querySelectorAll('[data-filter-apply-text]');
    this.selectedBars = this.querySelectorAll('[data-filter-selected-bar]');
    this.selectedBarTexts = this.querySelectorAll(
      '[data-filter-selected-text]'
    );
    this.filterForm = this.querySelector('[data-filter-form]');
    this.productGrid = document.querySelector('[data-product-grid]');
    this.loadMore = document.querySelector('load-more');
    this.collectionUrl = this.dataset.collectionUrl;
    this.showSelectionText = this.dataset.showSelectionText;
    this.selectedFiltersText = this.dataset.selectedFiltersText;
    this.filterIconPlus = document.querySelector('[data-filter-icon-plus]');
    this.filterIconMinus = document.querySelector('[data-filter-icon-minus]');

    this.initCurrentState();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  initCurrentState() {
    const url = new URL(window.location.href);

    // Read current sort
    this.currentSort = url.searchParams.get('sort_by') || '';

    // Read current filters from URL
    url.searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        if (!this.activeFilters.has(key)) {
          this.activeFilters.set(key, new Set());
        }
        this.activeFilters.get(key).add(value);
      }
    });

    // Mark active radio buttons
    this.syncUIFromState();
  }

  syncUIFromState() {
    // Set sort radio
    if (this.currentSort) {
      const sortRadio = this.querySelector(
        `input[name="sort_by"][value="${CSS.escape(this.currentSort)}"]`
      );
      if (sortRadio) sortRadio.checked = true;
    }

    // Set filter checkboxes
    this.activeFilters.forEach((values, key) => {
      values.forEach((value) => {
        const input = this.querySelector(
          `input[name="${CSS.escape(key)}"][value="${CSS.escape(value)}"]`
        );
        if (input) input.checked = true;
      });
    });

    this.updateUI();
  }

  bindEvents() {
    const { signal } = this.controller;

    // Open triggers (from collection toolbar)
    document.querySelectorAll('[data-filter-toggle]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.open(), { signal });
    });

    // Close
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close(), {
        signal,
      });
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close(), { signal });
    }

    // ESC key
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      },
      { signal }
    );

    // Clear all
    this.clearButtons.forEach((btn) => {
      btn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          this.clearAll();
        },
        { signal }
      );
    });

    // Apply / show selection
    this.applyButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.apply(), { signal });
    });

    // Listen for input changes to update count
    if (this.filterForm) {
      this.filterForm.addEventListener('change', () => this.onFilterChange(), {
        signal,
      });
    }
  }

  open() {
    this.isOpen = true;
    this.panel.classList.remove('translate-x-full');
    this.panel.classList.add('translate-x-0');
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.focus();

    if (this.overlay) {
      this.overlay.classList.remove('opacity-0', 'pointer-events-none');
      this.overlay.classList.add('opacity-100');
    }

    document.body.style.overflow = 'hidden';
    document.addEventListener('touchmove', this._preventScroll, {
      passive: false,
    });

    if (this.filterIconPlus) {
      this.filterIconPlus.classList.add('hidden');
      this.filterIconPlus.classList.remove('block');
    }
    if (this.filterIconMinus) {
      this.filterIconMinus.classList.remove('hidden');
      this.filterIconMinus.classList.add('block');
    }
  }

  close() {
    this.isOpen = false;
    this.panel.classList.remove('translate-x-0');
    this.panel.classList.add('translate-x-full');
    this.panel.setAttribute('aria-hidden', 'true');

    if (this.overlay) {
      this.overlay.classList.remove('opacity-100');
      this.overlay.classList.add('opacity-0', 'pointer-events-none');
    }

    document.body.style.overflow = '';
    document.removeEventListener('touchmove', this._preventScroll);

    if (this.filterIconPlus) {
      this.filterIconPlus.classList.add('block');
      this.filterIconPlus.classList.remove('hidden');
    }
    if (this.filterIconMinus) {
      this.filterIconMinus.classList.remove('block');
      this.filterIconMinus.classList.add('hidden');
    }
  }

  buildFilterUrl() {
    const url = new URL(this.collectionUrl, window.location.origin);

    // Add sort
    const sortInput = this.filterForm.querySelector(
      'input[name="sort_by"]:checked'
    );
    if (sortInput && sortInput.value) {
      url.searchParams.set('sort_by', sortInput.value);
    }

    // Add filters
    const filterInputs = this.filterForm.querySelectorAll(
      'input[type="checkbox"]:checked'
    );
    filterInputs.forEach((input) => {
      url.searchParams.append(input.name, input.value);
    });

    return url;
  }

  async onFilterChange() {
    this.updateUI();
    // Abort any in-flight filter count request
    if (this.filterAbort) this.filterAbort.abort();
    this.filterAbort = new AbortController();

    await this.fetchFilteredCount(this.filterAbort.signal);
  }

  updateUI() {
    const checkedFilters = this.filterForm
      ? this.filterForm.querySelectorAll('input[type="checkbox"]:checked')
      : [];
    const hasFilters = checkedFilters.length > 0;

    // Update selected bars
    const labels = hasFilters
      ? Array.from(checkedFilters).map((input) => {
          const label = input.closest('label');
          const textEl = label
            ? label.querySelector('[data-filter-label]')
            : null;
          return textEl ? textEl.textContent.trim() : input.value;
        })
      : [];
    this.selectedBars.forEach((bar) => {
      if (hasFilters) {
        bar.classList.remove('hidden');
      } else {
        bar.classList.add('hidden');
      }
    });
    this.selectedBarTexts.forEach((text) => {
      text.textContent = hasFilters
        ? `${this.selectedFiltersText} ${labels.join(', ')}`
        : '';
    });

    // Update all apply buttons (mobile + desktop)
    const hasAnySelection =
      hasFilters ||
      this.filterForm.querySelector('input[name="sort_by"]:checked');
    this.applyButtons.forEach((btn) => {
      if (hasAnySelection) {
        btn.classList.remove('!bg-grey-medium');
        btn.classList.add('!bg-button');
        btn.disabled = false;
      } else {
        btn.classList.add('!bg-grey-medium');
        btn.classList.remove('!bg-button');
        btn.disabled = true;
      }
    });
  }

  async fetchFilteredCount(signal) {
    const url = this.buildFilterUrl();
    url.searchParams.set('sections', 'collection');

    try {
      const response = await fetch(url, {
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) return;

      const html = await response.text();
      let sectionHtml = html;

      // If sections API returns JSON, parse it
      try {
        const json = JSON.parse(html);
        if (json['collection']) {
          sectionHtml = json['collection'];
        }
      } catch (e) {
        // Not JSON, use raw HTML
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');

      // Get the product count from the paginate data
      const loadMoreEl = doc.querySelector('load-more');
      const totalProducts = loadMoreEl
        ? parseInt(loadMoreEl.dataset.totalProducts, 10) || 0
        : doc.querySelectorAll('[data-product-card]').length;

      // Update button text with count
      if (totalProducts >= 0) {
        this.applyButtonTexts.forEach((text) => {
          text.textContent = `${this.showSelectionText} (${totalProducts})`;
        });
        this.applyButtons.forEach((btn) => {
          btn.disabled = totalProducts <= 0;
          btn.classList.toggle('!bg-grey-medium', totalProducts <= 0);
          btn.classList.toggle('!bg-button', totalProducts > 0);
        });
      }

      // Update disabled states based on new filter availability
      this.updateFilterAvailability(doc);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching filter count:', error);
      }
    }
  }

  updateFilterAvailability(doc) {
    // Get the new filter form from the fetched HTML
    const newFilterForm = doc.querySelector('[data-filter-form]');
    if (!newFilterForm) return;

    // Update each filter option's disabled/available state
    const currentOptions = this.filterForm.querySelectorAll(
      '[data-filter-option]'
    );
    currentOptions.forEach((option) => {
      const input = option.querySelector('input');
      if (!input || input.name === 'sort_by') return;

      const matchingOption = newFilterForm.querySelector(
        `input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`
      );

      if (matchingOption) {
        const newOption = matchingOption.closest('[data-filter-option]');
        const isDisabled = newOption
          ? newOption.dataset.filterDisabled === 'true'
          : false;

        if (isDisabled) {
          option.dataset.filterDisabled = 'true';
          input.disabled = true;
          option.classList.add('cursor-not-allowed');
          option.classList.remove('cursor-pointer');
        } else {
          option.dataset.filterDisabled = 'false';
          input.disabled = false;
          option.classList.remove('cursor-not-allowed');
          option.classList.add('cursor-pointer');
        }
      }
    });
  }

  clearAll() {
    if (!this.filterForm) return;

    // Uncheck all inputs
    this.filterForm
      .querySelectorAll('input[type="checkbox"]')
      .forEach((input) => {
        input.checked = false;
      });
    this.filterForm.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
    });

    this.activeFilters.clear();
    this.currentSort = '';
    this.updateUI();

    // Reset button text
    this.applyButtonTexts.forEach((text) => {
      text.textContent = this.showSelectionText;
    });

    this.applyButtons.forEach((btn) => {
      btn.classList.remove('!bg-grey-medium');
      btn.classList.add('!bg-button');
      btn.disabled = false;
    });

    // Reset disabled states
    this.filterForm
      .querySelectorAll('[data-filter-option]')
      .forEach((option) => {
        const input = option.querySelector('input');
        if (input && input.name !== 'sort_by') {
          option.dataset.filterDisabled = 'false';
          input.disabled = false;
          option.classList.remove('cursor-not-allowed');
          option.classList.add('cursor-pointer');
        }
        const label = option.querySelector('[data-filter-label]');
        if (label) {
          label.classList.remove('line-through');
          label.classList.remove('text-grey-dark');
        }
        const checkboxIcon = option.querySelector(
          '[data-filter-checkbox-icon]'
        );
        if (checkboxIcon) {
          checkboxIcon.classList.add('peer-checked:[&>span]:scale-100');
          checkboxIcon.classList.add('text-[#757575]');
        }
      });
  }

  apply() {
    const url = this.buildFilterUrl();
    this.close();
    window.location.href = url.toString();
  }
}

if (!customElements.get('collection-filter')) {
  customElements.define('collection-filter', CollectionFilter);
}
